import {
  MESSAGE_RECEIVED,
  CHAT_LIST_UPDATE,
  CHAT_LIST_ADD,
  CHAT_REMOVE,
  USER_TYPING,
  MESSAGE_SEEN,
  MESSAGE_EDIT,
  MESSAGE_DELETE,
  CHAT_MEMBERS_UPDATE,
} from "../constants/chatConstants";
import {
  NOTIFICATION_READ,
  NOTIFICATION_RECEIVED,
} from "../features/notifications/model/notificationConstants";
import { markNotificationRead } from "../features/notifications/model/notificationActions";
import {
  PRESENCE_SNAPSHOT,
  PRESENCE_UPDATE,
} from "../features/presence/model/presenceConstants";
import {
  normalizePresenceRow,
  normalizePresenceSnapshot,
} from "../features/presence/lib/normalizePresence";
import { syncPresenceLeaseTimer } from "../features/presence/lib/presenceLeaseTimers";
import { selectPresenceByUserId } from "../features/presence/model/presenceSelectors";
import { selectIdentity } from "../selectors/userSelectors";
import { resolveEntityShareRequest } from "../features/chat/entityShareRequests";
import { toast } from "react-toastify";

/**
 * Timer state must follow the presence row the reducer actually accepted.
 * Syncing from the inbound frame would let a rejected stale shorter lease
 * cancel the fresher authoritative expiry timer.
 */
function syncAcceptedPresenceLeaseTimer(userId, dispatch, getState) {
  if (userId == null) return;
  const accepted = selectPresenceByUserId(getState(), userId);
  syncPresenceLeaseTimer(accepted, dispatch);
}

const BLOCKED_DETAIL_UK = "Ви не можете надіслати повідомлення цьому користувачу.";

function resolveWsErrorDetail(payload) {
  if (!payload) return "Сталася помилка чату.";
  if (payload.code === "blocked" || payload.reason === "blocked") {
    return payload.detail || BLOCKED_DETAIL_UK;
  }
  return payload.detail || "Дію заборонено.";
}

function isNonNegativeSafeInt(value) {
  return Number.isSafeInteger(value) && value >= 0;
}

function isValidNotificationId(id) {
  return Number.isSafeInteger(id) && id > 0;
}

function resolveActiveChatIdFromPath(pathname) {
  if (typeof pathname !== "string") return null;
  const match = pathname.match(/^\/chats\/([^/]+)\/?$/);
  return match ? match[1] : null;
}

/**
 * Active-chat attention suppression requires a foreground/visible document.
 * Background tabs left on /chats/:id must not auto mark-read.
 *
 * visibilityState is required. hasFocus is applied only when the API exists
 * and returns a boolean — unavailable hosts (some PWA/test shells) skip it.
 */
function isDocumentForegroundVisible() {
  if (typeof document === "undefined") return false;
  if (document.visibilityState !== "visible") return false;
  if (typeof document.hasFocus === "function") {
    try {
      const focused = document.hasFocus();
      if (typeof focused === "boolean" && !focused) {
        return false;
      }
    } catch (_err) {
      // Feature present but unusable — fall back to visibilityState alone.
    }
  }
  return true;
}

function notificationTargetChatId(notification) {
  if (!notification || typeof notification !== "object") return null;
  const raw = notification.target_chat;
  if (raw == null || raw === "") return null;
  return String(raw);
}

function notificationLatestMessageId(notification) {
  if (!notification || typeof notification !== "object") return null;
  const payload = notification.payload;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  const raw = payload.latest_message_id;
  if (raw == null || raw === "") return null;
  return String(raw);
}

function parseNotificationEvent(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  const notification = payload.notification;
  if (!notification || typeof notification !== "object" || Array.isArray(notification)) {
    return null;
  }
  if (!isValidNotificationId(notification.id)) {
    return null;
  }
  if (!isNonNegativeSafeInt(payload.unread_count)) {
    return null;
  }
  return {
    notification,
    unreadCount: payload.unread_count,
  };
}

function parseNotificationReadEvent(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  if (!("ids" in payload)) {
    return null;
  }
  const { ids } = payload;
  if (ids !== null && !Array.isArray(ids)) {
    return null;
  }
  if (Array.isArray(ids)) {
    for (const id of ids) {
      if (!isValidNotificationId(id)) {
        return null;
      }
    }
  }
  if (!isNonNegativeSafeInt(payload.unread_count)) {
    return null;
  }
  return {
    ids,
    unreadCount: payload.unread_count,
  };
}

export const handleEvent = (data, dispatch, getState, send) => {
  const state = getState();
  const myUserId = selectIdentity(state)?.userId ?? null;

  switch (data.event) {
    case "message":
      dispatch({
        type: MESSAGE_RECEIVED,
        payload: data.payload,
        meta: { myUserId },
      });

      dispatch({
        type: CHAT_LIST_UPDATE,
        payload: data.payload,
        meta: { myUserId },
      });

      if (data.request_id) {
        resolveEntityShareRequest(data.request_id, {
          ok: true,
          payload: data.payload,
        });
      }
      break;

    case "typing":
      if (String(data.payload.user.id) === String(myUserId)) { return; }
      dispatch({
        type: USER_TYPING,
        payload: {
          chatId: data.payload.chat_id,
          user: data.payload.user,
        },
      });
      break;

    case "seen":
      dispatch({
        type: MESSAGE_SEEN,
        payload: data.payload,
      });
      break;

    case "message_edit":
      dispatch({
        type: MESSAGE_EDIT,
        payload: data.payload,
      });
      break;

    case "message_delete":
      dispatch({
        type: MESSAGE_DELETE,
        payload: data.payload,
      });
      break;

    case "chat_joined":
      dispatch({
        type: CHAT_LIST_ADD,
        payload: data.payload,
      });

      if (send) {
        send({
          event: "subscribe_chat",
          chat_id: data.payload.id,
        });
      }
      break;

    case "chat_removed":
      dispatch({
        type: CHAT_REMOVE,
        payload: data.payload.chat_id,
      });
      break;

    case "chat_members_update":
      dispatch({
        type: CHAT_MEMBERS_UPDATE,
        payload: data.payload,
      });
      break;

    case "error": {
      const detail = resolveWsErrorDetail(data.payload);
      const requestId = data.payload?.request_id;
      const handledShare =
        data.payload?.action === "send_entity_share"
        && requestId
        && resolveEntityShareRequest(requestId, {
          ok: false,
          detail,
          payload: data.payload,
        });
      if (!handledShare) {
        toast.error(detail);
      }
      break;
    }

    case "notification": {
      const parsed = parseNotificationEvent(data.payload);
      if (!parsed) {
        return;
      }
      const notification = parsed.notification;
      const targetChatId = notificationTargetChatId(notification);
      const activeChatId =
        typeof window !== "undefined"
          ? resolveActiveChatIdFromPath(window.location.pathname)
          : null;
      const suppressAttention =
        notification.type === "chat.message"
        && targetChatId
        && activeChatId
        && String(targetChatId) === String(activeChatId)
        && isDocumentForegroundVisible();

      dispatch({
        type: NOTIFICATION_RECEIVED,
        payload: parsed,
      });

      // Active-chat attention suppression (N8 / R1-B1/B2): same chat +
      // foreground document only; version-guard mark-read via
      // expected_latest_message_id so delayed ABA cannot clear a newer row.
      if (suppressAttention && isValidNotificationId(notification.id)) {
        const expectedLatestMessageId = notificationLatestMessageId(notification);
        dispatch(
          markNotificationRead(notification.id, {
            expectedLatestMessageId,
          }),
        );
      }
      break;
    }

    case "notification_read": {
      const parsed = parseNotificationReadEvent(data.payload);
      if (!parsed) {
        return;
      }
      dispatch({
        type: NOTIFICATION_READ,
        payload: parsed,
      });
      break;
    }

    case "presence_snapshot": {
      const rows = normalizePresenceSnapshot(data.payload);
      if (!rows.length && !Array.isArray(data.payload?.presences)) {
        return;
      }
      dispatch({
        type: PRESENCE_SNAPSHOT,
        payload: rows,
      });
      const seen = new Set();
      rows.forEach((row) => {
        if (row.userId == null || seen.has(row.userId)) return;
        seen.add(row.userId);
        syncAcceptedPresenceLeaseTimer(row.userId, dispatch, getState);
      });
      break;
    }

    case "presence_update": {
      const row = normalizePresenceRow(data.payload);
      if (!row) {
        return;
      }
      dispatch({
        type: PRESENCE_UPDATE,
        payload: row,
      });
      syncAcceptedPresenceLeaseTimer(row.userId, dispatch, getState);
      break;
    }

    default:
      break;
  }
};
